import cn from "classnames"

import { useCreateKey } from "@/api/useAuthKeys"
import { Text } from "@/atoms"
import { EVENTS } from "@/const/event.constants"
import { TAB_KEYS_BY_PAGE } from "@/const/tab.constants"
import { Permission } from "@/domain/user"
import { ExternalLinkMenuItem } from "@/molecules/ExternalLinkMenuItem"
import { LabelFeatureStatusTag } from "@/molecules/FeatureStatusTag"
import { useAuthKeys } from "@/pages/SettingsPage/application/auth/useAuthKeys"
import { AuthenticationMethodTab } from "@/pages/SettingsPage/components/organisms/AuthenticationMethodTab"
import { AuthKeysTab } from "@/pages/SettingsPage/components/organisms/AuthKeysTab"
import { CodeScanSastTab } from "@/pages/SettingsPage/components/organisms/CodeScanSastTab"
import { KaiTab } from "@/pages/SettingsPage/components/organisms/FormKaiTab"
import { GuidanceKaiTab } from "@/pages/SettingsPage/components/organisms/GuidanceKaiTab"
import { LabelsTab } from "@/pages/SettingsPage/components/organisms/LabelsTab"
import { ScopesTab } from "@/pages/SettingsPage/components/organisms/ScopesTab"
import { UsersTab } from "@/pages/SettingsPage/components/organisms/UsersTab"
import { filterItems, getItems } from "@/pages/SettingsPage/helpers/menu"

import styles from "./SettingsPage.module.scss"

import type { TFunction } from "@/hooks/useTranslation"

type Item = {
  label: string
  permission?: Permission
  key?: string
  isBeta?: boolean
}
type LabelProps = { activeKey?: string }

const getClasses = (key: string, activeKey?: string) =>
  cn(styles.title, activeKey === key && styles.active)

const getSectionTitle = (
  item: Item,
  featureFlags: Partial<Features.Flags> & { admin?: boolean } = {}
) => ({
  key: item.key || item.label,
  permission: undefined,
  label: <Text className={styles.titleInTab}>{item.label}</Text>,
  disabled: true,
  ...featureFlags
})

export const getLabel = (
  item: Item,
  props: LabelProps & Partial<Features.Flags>
) => {
  const { activeKey, ...featureFlags } = props
  const { permission, label, key = label, isBeta } = item
  const className = getClasses(key, activeKey)

  return {
    key,
    permission,
    label: isBeta ? (
      <LabelFeatureStatusTag
        type="beta"
        justify="space-between"
        label={<Text className={className}>{label}</Text>}
      />
    ) : (
      <Text className={className}>{label}</Text>
    ),
    ...featureFlags
  }
}

export const getItemsTabsByRole = (
  hasPermission: (permission: Permission) => boolean | undefined,
  {
    activeKey,
    t
  }: {
    activeKey: undefined | string
    t: TFunction
  }
) => {
  const TAB_KEYS = TAB_KEYS_BY_PAGE.SETTINGS
  const defaultItems = getItems(t)

  return filterItems(
    [
      { children: null },
      { children: <AuthenticationMethodTab /> },
      { children: <UsersTab /> },
      { children: null },
      { children: <KaiTab /> },
      { children: <GuidanceKaiTab /> },
      { children: null },
      { children: <ScopesTab /> },
      { children: <LabelsTab /> },
      { children: <CodeScanSastTab /> },
      { children: null },
      {
        children: (
          <AuthKeysTab
            tabName={TAB_KEYS[4]}
            title={t("apikeys.title")}
            description={t("apikeys.description")}
            useAuthKeys={useAuthKeys}
            useCreateKey={useCreateKey}
            keyType="API_KEYS"
            analyticsEvents={EVENTS.ANALYTIC_EVENTS.SETTINGS.AUTH_KEYS.API_KEYS}
          />
        )
      },
      {
        children: (
          <AuthKeysTab
            tabName={TAB_KEYS[5]}
            title={t("deploymentkeys.title")}
            description={t("deploymentkeys.description")}
            useAuthKeys={useAuthKeys}
            useCreateKey={useCreateKey}
            keyType="DEPLOYMENT_KEYS"
            analyticsEvents={
              EVENTS.ANALYTIC_EVENTS.SETTINGS.AUTH_KEYS.DEPLOYMENT_KEYS
            }
          />
        )
      },
      {
        children: (
          <AuthKeysTab
            tabName={TAB_KEYS[6]}
            title={t("servicekeys.title")}
            description={t("servicekeys.description")}
            useAuthKeys={useAuthKeys}
            useCreateKey={useCreateKey}
            keyType="SERVICE_KEYS"
            analyticsEvents={
              EVENTS.ANALYTIC_EVENTS.SETTINGS.AUTH_KEYS.SERVICE_KEYS
            }
          />
        )
      },
      { children: null },
      { children: null }
    ].map((item, index) => {
      if (item.children) {
        return { ...getLabel(defaultItems[index], { activeKey }), ...item }
      }

      const externalLink = defaultItems[index]?.externalLink

      if (externalLink) {
        const { key, label } = defaultItems[index]

        return {
          ...defaultItems[index],
          label: (
            <Text className={getClasses(key, activeKey)}>
              <ExternalLinkMenuItem href={externalLink} text={label} />
            </Text>
          )
        }
      }

      return getSectionTitle(defaultItems[index])
    }),
    hasPermission
  )
}
